"use client";

import type { ReactNode } from "react";
import { useSession } from "./SessionProvider";

export interface SessionGateProps {
  /**
   * Content to show when user is not authenticated
   */
  fallback: ReactNode;
  
  /**
   * Content to show when user is authenticated
   */
  children: ReactNode;
  
  /**
   * Optional loader to show while session is being hydrated
   */
  loader?: ReactNode;
}

/**
 * SessionGate conditionally renders children based on authentication state
 * Shows fallback (e.g., login form) when not authenticated
 * Shows children (protected content) when authenticated
 */
export function SessionGate({ fallback, children, loader }: SessionGateProps): React.ReactElement {
  const { session, isHydrated } = useSession();

  // Show loader while hydrating if provided
  if (!isHydrated && loader) {
    return <>{loader}</>;
  }

  // Show fallback if not authenticated
  if (!session) {
    return <>{fallback}</>;
  }

  // Show protected content
  return <>{children}</>;
}

